import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useMutation, useQueryClient } from "react-query";
import {
  getVal,
  SANS_3,
  setVal,
  UI_BODY_SEMIBOLD_SM,
  UI_BODY_SM,
  ButtonPrimaryV2,
  UI_CAPTION,
  UI_CAPTION_BOLD,
  Divider,
  DropdownMulti,
  colors2,
  ButtonSecondaryCompact,
} from "oolib";
import { AIMessage } from "@/components/AiChat/comps/Messages/AI";
import { getDefaultAgentVersion } from "@/components/AiChat/agentVersion";
import { LoadingText } from "@/components/AiChat/comps/Messages/Loader";
import { SidebarProvider } from "@/components/AiChat/contexts/sidebarContext";
import { WidgetHeader } from "../WidgetBarChart/WIDGET_ELEM_ConfigForm";
import { useUpdateLoadedWidgetsTracker } from "../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { useReportLandingContext } from "../../../ReportLandingContext";
import { WidgetErrorState } from "../../WidgetRenderer/comps/altStatePlaceholders";
import { useAppSettingsContext } from "@/contexts/appSettingsContext";
import styled from "styled-components";
import LexicalTextEditor from "@/components/inputs/TextEditor/LexicalTextEditor";
import { WidgetFilters } from "../utils/WidgetFilters";
import { createContentCardDate } from "@/utils/momentManipulate";

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const SearchBarStyled = styled.div`
  width: 100%;
  padding: 1.2rem;
  background: #ffffff;
  border-radius: 1rem;
  min-height: 7rem;
  border: 1px solid #0000001a;
  box-shadow: 0px 4px 10px 0px #0000000f;

  :focus-within {
    border: 1px solid #184469;
  }
`;

// Hook to call getResponse endpoint.
// Backend computes widgetAskAICacheKey from (query + contentTypes + filters)
// and returns a cached response on hit, or runs the LLM on miss.
const useAskAIMutation = () =>
  useMutation(({ query, activeLang, immutableActiveFilters, immutableContentTypes, skipCache }) =>
    axios
      .post(
        `/api/aiChat/getResponse/new`,
        { immutableActiveFilters, immutableContentTypes },
        {
          params: {
            query,
            activeLang,
            ACLScope: 'reports',
            // Omitting this silently ran V1 — the backend derives the version
            // purely from this param. Shared with the AI Chat page's default.
            aiChatAgent: getDefaultAgentVersion(),
            ...(skipCache ? { skipCache: 'true' } : {}),
          },
        }
      )
      .then((res) => res.data)
  );

export const Component = ({
  id,
  query,
  widgetIdx,
  style = {},
  activeFilters = [],
  widgetFilters = [],
  selectedContentTypes,
  // Public-share replay: a frozen response blob ({ finalMessage, chatId, createdAt }).
  // When present the widget renders it instead of calling the LLM. See SHARED_REPORTS_DESIGN.md.
  prefetchedData,
  // True only on the public report page — hides interactive/reference UI.
  isPublicReport = false,
}) => {
  const { APP_SETTINGS } = useAppSettingsContext();
  const activeLang = APP_SETTINGS?.lang?.value || "en";
  const hasMutatedRef = useRef(false);
  const { updateWidgetDataStore } = useReportLandingContext() || {};

  const {
    mutate,
    data: chatDataFromApiCall,
    status: mutationStatusFromApiCall,
    error: mutationError,
  } = useAskAIMutation();

  // On replay, render the frozen response; the LLM call is skipped entirely.
  const chatData = prefetchedData || chatDataFromApiCall;
  const mutationStatus = prefetchedData ? "success" : mutationStatusFromApiCall;

  const mutationArgs = useMemo(() => ({
    query,
    activeLang,
    immutableActiveFilters: widgetFilters,
    immutableContentTypes: selectedContentTypes?.length > 0 ? selectedContentTypes : undefined,
  }), [query, activeLang, widgetFilters, selectedContentTypes]);

  // On mount, call the endpoint (unless replaying a snapshot).
  // Backend handles caching transparently via widgetAskAICacheKey.
  useEffect(() => {
    if (query && !hasMutatedRef.current && !prefetchedData) {
      hasMutatedRef.current = true;
      mutate(mutationArgs);
    }
  }, [query, activeLang]);

  // Capture: push the response to the central store so it can be frozen into a share
  // snapshot. No-op on replay (prefetchedData is already the captured response).
  useEffect(() => {
    if (!prefetchedData && mutationStatus === "success" && chatDataFromApiCall) {
      updateWidgetDataStore && updateWidgetDataStore(id, chatDataFromApiCall);
    }
  }, [prefetchedData, mutationStatus, chatDataFromApiCall, id, updateWidgetDataStore]);

  const handleRegenerate = () => {
    mutate({ ...mutationArgs, skipCache: true });
  };

  useUpdateLoadedWidgetsTracker({ status: mutationStatus, widgetIdx });

  if (!query) {
    return <SANS_3>No query configured</SANS_3>;
  }

  if (mutationStatus === "loading" || mutationStatus === "idle") {
    return (
      <StyledWrapper style={{ padding: '1.6rem', ...style }}>
        <LoadingText />
      </StyledWrapper>
    );
  }

  if (mutationStatus === "error") {
    return (
      <WidgetErrorState
        error={mutationError?.response?.data?.message || mutationError?.message || "Failed to get AI response"}
      />
    );
  }

  const aiMessage = chatData?.finalMessage;
  const activeChatId = chatData?.chatId;
  const createdAt = chatData?.createdAt;

  return (
    <StyledWrapper style={style}>
      {/* Response metadata bar */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "0.8rem",
        padding: "0.6rem 1.6rem",
        borderBottom: `1px solid ${colors2.grey10}`,
        background: "white",
        position: "sticky",
        top: 0,
        zIndex: 1,
      }}>
        <UI_CAPTION style={{ color: colors2.grey40 }}>
          {createdAt ? `Generated ${createContentCardDate(createdAt)}` : "AI-generated response"}
        </UI_CAPTION>
        {!isPublicReport && (
          <ButtonSecondaryCompact
            icon="ArrowsClockwise"
            onClick={handleRegenerate}
          >
            Regenerate
          </ButtonSecondaryCompact>
        )}
      </div>

      {activeFilters.length > 0 && (
        <div style={{ padding: '0.8rem 1.6rem 0' }}>
          <UI_CAPTION style={{ color: colors2.grey40 }}>
            Report-level filters do not affect AI responses. Use widget-level filters instead.
          </UI_CAPTION>
        </div>
      )}
      {aiMessage ? (
        <SidebarProvider>
          {/* On the public report page, hide references — they fetch cited source docs
              a public viewer can't load/access (only the answer text is frozen). */}
          <AIMessage
            message={aiMessage}
            activeChatId={activeChatId}
            noBorderShadow
            directLinkReferences={!isPublicReport}
            hideReferencesAndActions={isPublicReport}
          />
        </SidebarProvider>
      ) : (
        <SANS_3>No response received</SANS_3>
      )}
    </StyledWrapper>
  );
};

export const optionDef = {
  display: "Ask AI",
  icon: "Sparkle",
  value: "askAI",
  comp: "WidgetAskAI",
  widgetLabel: "Ask AI",
};

export const someReqPropsUnset = (config) => {
  return !config?.props?.query || config.props.query.trim() === "";
};

export const ConfigForm = ({
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx,
}) => {
  const [inputValue, setInputValue] = useState({});
  const [editorKey, setEditorKey] = useState(false);
  const editorRef = useRef();
  const { APP_SETTINGS } = useAppSettingsContext();
  const activeLang = APP_SETTINGS?.lang?.value || "en";

  const queryClient = useQueryClient();
  const allTpls = queryClient.getQueryData("allTpls");

  const publishingTpls = useMemo(
    () => (allTpls || []).filter(tpl => tpl.general?.segment === 'publishing'),
    [allTpls]
  );

  const tplDropdownOptions = useMemo(
    () => publishingTpls.map(d => ({
      display: d.general?.content?.title || d.kp_content_type,
      value: d.kp_content_type,
    })),
    [publishingTpls]
  );

  // Draft state — changes here do NOT persist to widget config until "Generate" succeeds
  const savedQuery = getVal(thisWidgetConfig, 'props.query') || "";
  const savedContentTypes = getVal(thisWidgetConfig, 'props.selectedContentTypes') || [];

  const [draftContentTypes, setDraftContentTypes] = useState(savedContentTypes);

  const draftContentTypesDropdownValues = useMemo(
    () => draftContentTypes.map(ct => {
      const tpl = publishingTpls.find(d => d.kp_content_type === ct);
      return {
        display: tpl?.general?.content?.title || ct,
        value: ct,
      };
    }),
    [draftContentTypes, publishingTpls]
  );

  // WidgetFilters writes directly to widget config (props.widgetFilters).
  // We read the current value as the "draft" for generating.
  const draftWidgetFilters = getVal(thisWidgetConfig, 'props.widgetFilters') || [];

  /**
   * Write several props in ONE setConfig call.
   *
   * One call per prop does not work under report collab. `applyConfig`
   * resolves an updater against `liveConfig`, which does not change between
   * synchronous calls, and a content edit writes the widget's WHOLE entry —
   * so three calls in a row each start from the same stale base and each
   * overwrite the one before. Only the last prop survived, which is how a
   * generated answer ended up with no `props.query`: the widget read as
   * unconfigured and rendered the under-construction placeholder, with no
   * error anywhere. (With collab off React composes the updaters, so this
   * only ever broke where collab was live.)
   */
  const handleChangeProps = useCallback((patch) => {
    setConfig((prev) => {
      let thisWidgetModifiedConfig = JSON.parse(
        JSON.stringify(prev[underEditWidgetIdx])
      );
      for (const [key, value] of Object.entries(patch)) {
        thisWidgetModifiedConfig = setVal(thisWidgetModifiedConfig, key, value);
      }

      const modifiedFullConfig = JSON.parse(JSON.stringify(prev));
      modifiedFullConfig[underEditWidgetIdx] = thisWidgetModifiedConfig;
      return modifiedFullConfig;
    });
  }, [setConfig, underEditWidgetIdx]);

  const noUserText = inputValue.allText ? inputValue.allText.trim().length === 0 : true;

  /**
   * Save the question; the WIDGET asks it.
   *
   * This used to run the request here and persist `props.query` only in that
   * call's onSuccess. Two problems. The request takes minutes on a large
   * tenant (mining, then the writer, over tens of thousands of observations),
   * and react-query drops a per-call callback when the component unmounts —
   * so closing this panel, or selecting another widget, computed and cached an
   * answer that the config never learned about. With `props.query` unset,
   * someReqPropsUnset() below is true and the renderer draws the
   * under-construction placeholder: a finished answer, a blank widget, no
   * error anywhere. And when it did persist, the panel's request and the
   * widget's own mount request both ran, paying for the same answer twice.
   *
   * Now the config is the only thing written here. The widget requests on
   * mount, shows its own loading and error states, and the backend's
   * widgetAskAICacheKey means a repeat of the same question is free.
   */
  const handleSubmitQuestion = () => {
    if (noUserText) return;
    const queryText = inputValue.allText;

    handleChangeProps({
      "props.query": queryText,
      "props.title": queryText,
      "props.selectedContentTypes": draftContentTypes,
    });

    // Clear input
    setInputValue({});
    setEditorKey((prev) => !prev);
  };

  const handleChangeQuestion = () => {
    // Reset to allow entering a new question — does NOT clear persisted config
    // (persisted config only updates on successful generation)
    setInputValue({});
    setEditorKey((prev) => !prev);
    // Clear the saved query so the input field shows
    handleChangeProps({ "props.query": "", "props.title": "" });
  };

  const handleContentTypeSelection = (k, v) => {
    setDraftContentTypes(v.map(d => d.value));
  };

  const handleOnKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      setTimeout(handleSubmitQuestion, 0);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <WidgetHeader icon="Sparkle" title="Ask AI" />

      <div>
        <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: 500 }}>
          Your Question
        </label>
        {savedQuery ? (
          <div style={{
            padding: "12px",
            background: "#f5f5f5",
            borderRadius: "8px",
            marginBottom: "8px"
          }}>
            <UI_BODY_SEMIBOLD_SM>{savedQuery}</UI_BODY_SEMIBOLD_SM>
            <button
              onClick={handleChangeQuestion}
              style={{
                marginTop: "8px",
                padding: "4px 8px",
                fontSize: "12px",
                cursor: "pointer",
                border: "1px solid #ccc",
                borderRadius: "4px",
                background: "white"
              }}
            >
              Change Question
            </button>
          </div>
        ) : (
          <SearchBarStyled onClick={() => editorRef.current?.click()}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "1.2rem",
                minHeight: "4rem",
              }}
            >
              <div onKeyDown={handleOnKeyPress} style={{ flex: 1 }}>
                <div ref={editorRef}>
                  <LexicalTextEditor
                    key={editorKey}
                    placeholderColor={"#00000080"}
                    placeholder="Ask any question..."
                    value={inputValue}
                    variant={"simple"}
                    editorStyle={{ maxHeight: "120px", overflowY: "auto" }}
                    onChange={(id, value) => setInputValue({ ...value })}
                  />
                </div>
              </div>
              {!noUserText && (
                <ButtonPrimaryV2
                  onClick={handleSubmitQuestion}
                  icon="ArrowUp"
                />
              )}
            </div>
            <div style={{ color: "#00000080", marginTop: "4px" }}>
              <UI_CAPTION_BOLD>shift + enter</UI_CAPTION_BOLD>{" "}
              <UI_CAPTION>for new line</UI_CAPTION>
            </div>
          </SearchBarStyled>
        )}
      </div>

      <Divider style={{ margin: "0 -1.6rem" }}/>

      {/* Content Type Selector */}
      <div>
        <UI_BODY_SM style={{ marginBottom: "0.4rem" }}>
          Content Types
        </UI_BODY_SM>
        <DropdownMulti
          options={tplDropdownOptions}
          value={draftContentTypesDropdownValues}
          onChange={handleContentTypeSelection}
          popOutOfOverflowHiddenParent
        />
      </div>

      {/* Filters (auto-generated based on selected content types) */}
      <WidgetFilters
        widgetConfig={thisWidgetConfig}
        updateWidgetConfig={setConfig}
        widgetIndex={underEditWidgetIdx}
        contentTypesForFilters={draftContentTypes}
      />

      {/* No status here: the widget itself shows the answer, its loading
          state and any error. */}
    </div>
  );
};

export const defaultConfig = {
  props: {
    title: "Ask AI",
  },
};
